import type { UseQueryResult } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { useId, type ReactElement, type ReactNode } from 'react';

import { cn } from '@/components/ui/cn';
import { SKELETON_CYCLE, skeletonLine } from './skeleton';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

// Marco común de tarjetas (Estudio de tiempos so=420 y Panel del QMS so=430): encabezado
// con título y dato de contexto, y los estados de la consulta (esqueleto con la forma
// de la tarjeta, error accionable con Reintentar y vacío explicado).

interface FrameProps {
  title: string;
  count?: string;
  /** Acciones de la tarjeta a la derecha del encabezado (por ejemplo, Exportar). */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function CardFrame({
  title,
  count,
  actions,
  className,
  children,
}: FrameProps): ReactElement {
  const id = useId();
  return (
    <section className={cn('card rise', className)} aria-labelledby={id}>
      <div className="ch">
        <h3 id={id}>{title}</h3>
        {count !== undefined && <span className="count">{count}</span>}
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Nota de las tarjetas cuando el rango de fechas es inválido (el motivo va bajo los filtros). */
export const RANGE_NOTE = 'Corrige el rango de fechas para ver esta información.';

/** Aviso dentro de una tarjeta (vacío o rango inválido): texto sin tono de error. */
/** Estado vacío de una tarjeta: ícono y texto, centrados (mismo en todas las tarjetas). */
export function CardEmpty({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="cb card-empty" role="status">
      <Inbox aria-hidden="true" strokeWidth={1.6} />
      <p>{children}</p>
    </div>
  );
}

export function CardNote({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="cb">
      <p className="soft note-sm">{children}</p>
    </div>
  );
}

interface BodyProps<T> {
  query: UseQueryResult<T>;
  /** Filas del esqueleto: imita la cantidad de filas de la tarjeta. */
  rows?: number;
  isEmpty?: (data: T) => boolean;
  emptyText?: string;
  children: (data: T) => ReactNode;
}

export function QueryBody<T>({
  query,
  rows = 3,
  isEmpty,
  emptyText,
  children,
}: BodyProps<T>): ReactElement {
  // Una recarga que falla conserva lo que ya se veía (TanStack mantiene `data`) y lo avisa.
  const failure = query.isError ? (
    <div className="cb" role="alert">
      <p className="state-msg">
        {query.data === undefined
          ? `No se pudo cargar esta información. ${errorAdvice(query.error)}`
          : `No se pudo actualizar esta información. Se muestran los datos anteriores.${refreshAdvice(query.error)}`}
      </p>
      <button
        type="button"
        className="btn mut sm"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      >
        {query.isFetching ? 'Reintentando…' : 'Reintentar'}
      </button>
    </div>
  ) : null;
  if (query.data === undefined) {
    if (failure !== null) {
      return failure;
    }
    return (
      <div className="cb tsk" aria-busy="true">
        <span className="sr-only">Cargando</span>
        {Array.from({ length: rows }, (_, i) => (
          <span
            key={i}
            className={skeletonLine({
              width: SKELETON_CYCLE[i % SKELETON_CYCLE.length],
              height: 'sm',
            })}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }
  return (
    <>
      {isEmpty?.(query.data) === true ? <CardEmpty>{emptyText}</CardEmpty> : children(query.data)}
      {failure}
    </>
  );
}
